import React, {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactGA from "react-ga4";
import {
    actionToGetSEOMetaDataApiCall,
    actionToGetWebsiteContentApiCall,
} from "../../redux/action";
import useSEOData from "./useSEOData";

const HelmetComponent = () => {
    const dispatch = useDispatch();
    const { pathname,search } = useLocation();
    const companyData = useSelector((state) => state.webSetting.companyData);

    const seoData = useSEOData();
    const lastPathname = useRef();

    useEffect(() => {
        // Only run if pathname is different from lastPathname
        if (lastPathname.current !== pathname) {
            lastPathname.current = pathname;

            window.scrollTo(0, 0);
            dispatch(actionToGetSEOMetaDataApiCall({ pathname }));
            dispatch(actionToGetWebsiteContentApiCall({ pathname }));
        }
    }, [pathname, dispatch]);


    useEffect(() => {
        if (seoData) {
            ReactGA.send({ hitType: "pageview", page: pathname });
        }
    }, [pathname, seoData]);

    const siteUrl = "https://www.stemcity.in";
    const currentURL = window.location.href;
    const fullUrl = `${siteUrl}${pathname}${search}`;

    const defaultMeta = {
        title: "STEMCITY® – Changing the way of learning",
        description: "Explore STEMCITY® for DIY STEM, Robotics & AI kits! Hands-on learning for students to boost creativity & problem-solving. Shop now!",
        keywords: "DIY educational kits, STEM kits, robotics kits, AI kits, IOT kits, science projects, STEM education, hands-on learning",
        image: "https://stemcity.s3.ap-southeast-2.amazonaws.com/logo/stemcity.webp?v=1615"
    };

    const generateJSONLD = () => {
        const baseOrg = {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "STEMCITY",
            "url": siteUrl,
            "logo": defaultMeta.image,
            "sameAs": [
                "https://www.facebook.com/stemcityindia",
                "https://www.instagram.com/stemcityindia",
                "https://www.youtube.com/@stemcityindia"
            ]
        };

        const schema = [];

        // Blog schema
        if (pathname.startsWith("/blog/")) {
            const blogSchema = {
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                "headline": seoData?.meta_title || "STEMCITY Blog",
                "description": seoData?.meta_description || defaultMeta.description,
                "url": fullUrl,
                "image": seoData?.meta_image || defaultMeta.image,
                "author": {
                    "@type": "Organization",
                    "name": "STEMCITY"
                },
                "publisher": {
                    "@type": "Organization",
                    "name": "STEMCITY",
                    "logo": {
                        "@type": "ImageObject",
                        "url": defaultMeta.image
                    }
                }
            };

            if (seoData?.datePublished) {
                blogSchema.datePublished = seoData.datePublished;
            }
            if (seoData?.dateModified) {
                blogSchema.dateModified = seoData.dateModified;
            }

            schema.push(blogSchema);
        }

        // Product schema
        if (pathname.startsWith("/product/")) {
            schema.push({
                "@context": "https://schema.org",
                "@type": "Product",
                "name": seoData?.meta_title || "STEMCITY® Product",
                "image": seoData?.meta_image || defaultMeta.image,
                "description": seoData?.meta_description || defaultMeta.description,
                "brand": {
                    "@type": "Brand",
                    "name": "STEMCITY"
                },
                "url": fullUrl
            });
        }

        // Breadcrumb schema (applies to all pages)
        const breadcrumbParts = pathname.split('/').filter(Boolean);
        const breadcrumbSchema = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbParts.map((part, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "name": part.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), // Format nicely
                "item": `${siteUrl}/${breadcrumbParts.slice(0, index + 1).join('/')}`
            }))
        };
        schema.push(breadcrumbSchema);

        // Always include organization
        schema.push(baseOrg);

        return schema.length === 1 ? schema[0] : schema;
    };


    return (
        <>
            <Helmet htmlAttributes={{ lang: 'en' }}>
                <link
                    rel="preload"
                    as="image"
                    href="https://stemcity.s3.ap-southeast-2.amazonaws.com/logo/stemcity.webp?v=1615"
                    type="image/webp"
                />
                <link rel="preload" as="image" href={companyData?.logo} />
                <link rel="canonical" href={currentURL} />
                <title>{seoData?.meta_title || defaultMeta.title}</title>
                <meta name="description" content={seoData?.meta_description || defaultMeta.description} />
                <meta name="keywords" content={seoData?.meta_keyword || defaultMeta.keywords} />

                {/* Open Graph */}
                <meta property="og:url" content={fullUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="STEMCITY" />
                <meta property="og:title" content={seoData?.meta_title || defaultMeta.title} />
                <meta property="og:description" content={seoData?.meta_description || defaultMeta.description} />
                <meta property="og:image" content={seoData?.meta_image || defaultMeta.image} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoData?.meta_title || defaultMeta.title} />
                <meta name="twitter:description" content={seoData?.meta_description || defaultMeta.description} />
                <meta name="twitter:image" content={seoData?.meta_image || defaultMeta.image} />
            </Helmet>

            {/* Schema.org JSON-LD */}
            <script type="application/ld+json">
                {JSON.stringify(generateJSONLD())}
            </script>
        </>
    );
};

export default HelmetComponent;
